import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import { mmToPx } from './constants';
import type { CardSize, Drift, PaperSize } from '../types';

const TEXT = rgb(0.18, 0.16, 0.13);
const MUTED = rgb(0.45, 0.4, 0.32);

// Convert points (1 pt = 1/72 in) to our page's user units. The page is sized
// in 300-DPI pixels, so 1 pt = 300/72 user units. We express type sizes in
// real points so they print at the expected physical size.
const pt = (n: number) => Math.round((n * 300) / 72);

interface Line {
  text: string;
  size: number;
  bold?: boolean;
  gap?: number;
}

export const buildReadmePdf = async (
  pdf: PDFDocument,
  paper: PaperSize,
  card: CardSize,
  drift: Drift,
  hasInside: boolean
) => {
  const page = pdf.addPage([paper.widthPx, paper.heightPx]);
  const helv = await pdf.embedFont(StandardFonts.Helvetica);
  const bold = await pdf.embedFont(StandardFonts.HelveticaBold);

  const driftLabel =
    drift.dxMm === 0 && drift.dyMm === 0
      ? 'No drift offset applied.'
      : `Inside page shifted by dx=${drift.dxMm} mm, dy=${drift.dyMm} mm.`;

  const lines: Line[] = [
    { text: 'How to print this card', size: pt(28), bold: true, gap: pt(10) },
    { text: 'Settings', size: pt(15), bold: true, gap: pt(4) },
    { text: `· Paper: ${paper.label} (landscape)`, size: pt(12) },
    { text: `· Card: ${card.label}`, size: pt(12) },
    {
      text: `· ${hasInside ? '2-sided' : '1-sided'} print${hasInside ? ', short-edge flip' : ''}`,
      size: pt(12),
    },
    { text: '· Scale: 100% (do NOT "fit to page")', size: pt(12), gap: pt(10) },
    { text: 'Steps', size: pt(15), bold: true, gap: pt(4) },
    { text: '1. Open this PDF in your system print dialog.', size: pt(12) },
    { text: '2. Confirm scale is 100% and orientation is landscape.', size: pt(12) },
    {
      text: hasInside
        ? '3. Set 2-sided printing with "short-edge flip" (also called "flip on short edge" or "tablet").'
        : '3. Print single-sided.',
      size: pt(12),
    },
    { text: '4. Cut along the outer crop marks (the small ticks at each corner).', size: pt(12) },
    { text: '5. Fold along the dashed center line.', size: pt(12), gap: pt(10) },
    { text: 'Troubleshooting', size: pt(15), bold: true, gap: pt(4) },
    {
      text: '· Bad fold alignment: confirm your printer used 100% scale, not "fit to page".',
      size: pt(12),
    },
    {
      text: '· Inside shifted from outside: your printer has a duplex drift. Use the drift-calibration tool to measure it,',
      size: pt(12),
    },
    {
      text: '  then re-download this PDF with the drift offset baked into the inside page.',
      size: pt(12),
      gap: pt(10),
    },
    { text: driftLabel, size: pt(10) },
    { text: 'Generated by foldcardpdf.', size: pt(9) },
  ];

  let y = paper.heightPx - mmToPx(25);
  const x = mmToPx(22);
  const MUTED_THRESHOLD = pt(10);
  for (const line of lines) {
    const font = line.bold ? bold : helv;
    page.drawText(line.text, {
      x,
      y,
      size: line.size,
      font,
      color: line.size <= MUTED_THRESHOLD ? MUTED : TEXT,
    });
    y -= Math.round(line.size * 1.4);
    if (line.gap) y -= line.gap;
  }
};
